import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import { PencilLine, AlertCircle, Loader, ArrowRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { useUpdateOrgLimit } from '@/services/settings'
import { useActiveOrgProfile } from '@/hooks/useActiveOrgProfile'
import { toast } from 'react-hot-toast'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

type OrgStorageVolumeCardProps = {
  onLimitChange?: (limit: number, countLimit: number) => void
}

const storageVolumeFormSchema = z.object({
  storageLimit: z.number().min(1, 'Storage limit must be at least 1'),
  storageCountLimit: z
    .number()
    .min(1, 'Storage count limit must be at least 1'),
})

type StorageVolumeFormValues = z.infer<typeof storageVolumeFormSchema>

type CardState = {
  isEditing: boolean
  isConfirming: boolean
  showSuccess: boolean
}

export const OrgStorageVolumeCard = ({
  onLimitChange,
}: OrgStorageVolumeCardProps) => {
  const [state, setState] = useState<CardState>({
    isEditing: false,
    isConfirming: false,
    showSuccess: false,
  })

  const form = useForm<StorageVolumeFormValues>({
    resolver: zodResolver(storageVolumeFormSchema),
    defaultValues: {
      storageLimit: 0,
      storageCountLimit: 0,
    },
  })

  const { data: activeOrgProfile, isLoading } = useActiveOrgProfile()

  const currentStorageLimit =
    activeOrgProfile?.org?.metadata?.limits?.org_per_user_storage_gb || 0
  const currentStorageCountLimit =
    activeOrgProfile?.org?.metadata?.limits?.org_per_user_storage_count || 0

  const draftStorageLimit = form.watch('storageLimit')
  const draftStorageCountLimit = form.watch('storageCountLimit')

  // Calculate percentage changes
  const storageLimitPercentage = (() => {
    if (currentStorageLimit === 0 && draftStorageLimit === 0) return 0
    if (currentStorageLimit === 0 && draftStorageLimit > 0) return 100
    if (draftStorageLimit === 0) return -100
    return Math.round((draftStorageLimit / currentStorageLimit - 1) * 100)
  })()

  const storageCountPercentage = (() => {
    if (currentStorageCountLimit === 0 && draftStorageCountLimit === 0) return 0
    if (currentStorageCountLimit === 0 && draftStorageCountLimit > 0) return 100
    if (draftStorageCountLimit === 0) return -100
    return Math.round(
      (draftStorageCountLimit / currentStorageCountLimit - 1) * 100
    )
  })()

  const updateOrgLimitMutation = useUpdateOrgLimit()

  useEffect(() => {
    if (activeOrgProfile && !state.isEditing) {
      const newStorageLimit =
        activeOrgProfile.org.metadata.limits?.org_per_user_storage_gb || 0
      const newStorageCountLimit =
        activeOrgProfile.org.metadata.limits?.org_per_user_storage_count || 0

      form.setValue('storageLimit', newStorageLimit)
      form.setValue('storageCountLimit', newStorageCountLimit)

      if (onLimitChange) {
        onLimitChange(newStorageLimit, newStorageCountLimit)
      }
    }
  }, [activeOrgProfile, onLimitChange, form, state.isEditing])

  const onEditClick = () => {
    form.setValue('storageLimit', currentStorageLimit)
    form.setValue('storageCountLimit', currentStorageCountLimit)
    setState((prev) => ({ ...prev, isEditing: true }))
  }

  const onCancelClick = () => {
    setState((prev) => ({ ...prev, isEditing: false }))
  }

  const onConfirmClick = () => {
    if (form.formState.isValid) {
      setState((prev) => ({ ...prev, isConfirming: true }))
    }
  }

  const onConfirmUpdate = () => {
    updateOrgLimitMutation.mutate(
      {
        org_per_user_storage_gb: draftStorageLimit,
        org_per_user_storage_count: draftStorageCountLimit,
      },
      {
        onSuccess: () => {
          setState((prev) => ({
            ...prev,
            isEditing: false,
            isConfirming: false,
          }))
          if (onLimitChange) {
            onLimitChange(draftStorageLimit, draftStorageCountLimit)
          }
        },
        onError: (err) => {
          console.error('Failed to update org storage limits:', err)
          toast.error(JSON.stringify(err))
          setState((prev) => ({ ...prev, isConfirming: false }))
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="h-6 w-6 text-primary animate-spin mr-2" />
        <span>Loading storage volume limits...</span>
      </div>
    )
  }

  const { isEditing, isConfirming } = state

  return (
    <>
      {!isEditing ? (
        <ViewModeContent
          currentStorageLimit={currentStorageLimit}
          currentStorageCountLimit={currentStorageCountLimit}
          onEditClick={onEditClick}
        />
      ) : (
        <EditModeContent
          form={form}
          currentStorageLimit={currentStorageLimit}
          currentStorageCountLimit={currentStorageCountLimit}
          draftStorageLimit={draftStorageLimit}
          draftStorageCountLimit={draftStorageCountLimit}
          storageLimitPercentage={storageLimitPercentage}
          storageCountPercentage={storageCountPercentage}
          isPending={updateOrgLimitMutation.isPending}
          onCancelClick={onCancelClick}
          onConfirmClick={onConfirmClick}
        />
      )}

      <ConfirmationDialog
        open={isConfirming}
        onOpenChange={(open) =>
          setState((prev) => ({ ...prev, isConfirming: open }))
        }
        title="Update Organization-Wide Storage Volume Limit"
        description={
          <>
            You are about to update the organization-wide volume limits to{' '}
            <b className="text-foreground/90">{draftStorageLimit} GB storage</b>{' '}
            and{' '}
            <b className="text-foreground/90">
              {draftStorageCountLimit} storage volumes
            </b>{' '}
            per user.
            <p className="mt-2">
              This change will affect all future resources, while existing
              resources will continue running with the limits that were in
              effect when they were created.
            </p>
          </>
        }
        buttons={{
          confirm: {
            label: 'Apply to All Users',
            isDestructive: true,
            isLoading: updateOrgLimitMutation.isPending,
          },
        }}
        confirmation={{
          text: `${draftStorageLimit},${draftStorageCountLimit}`,
          prompt:
            "Please type the storage limit and volume count in format 'GB,Count' to confirm:",
        }}
        info={{
          warningPoints: [
            'All customized user limits will be overwritten for future resources.',
            'Existing resources will continue running based on the limits from when they were created.',
          ],
        }}
        onConfirm={onConfirmUpdate}
        onCancel={() => {
          setState((prev) => ({ ...prev, isConfirming: false }))
        }}
      />
    </>
  )
}

type ViewModeContentProps = {
  currentStorageLimit: number
  currentStorageCountLimit: number
  onEditClick: () => void
}

const ViewModeContent = ({
  currentStorageLimit,
  currentStorageCountLimit,
  onEditClick,
}: ViewModeContentProps) => (
  <div className="space-y-6">
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-muted-foreground">
          Current Limits
        </span>
        <Badge variant="outline" className="px-2 py-0 text-sm">
          Default
        </Badge>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold">{currentStorageLimit}</span>
        <span className="text-sm text-muted-foreground">GB per user</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold">{currentStorageCountLimit}</span>
        <span className="text-sm text-muted-foreground">
          storage volumes per user
        </span>
      </div>
    </div>

    <Button
      variant="outline"
      onClick={onEditClick}
      className="w-fit"
      data-testid="edit-org-storage-button"
    >
      <PencilLine className="mr-2 h-4 w-4" />
      Edit Organization Storage Limits
    </Button>
  </div>
)

type EditModeContentProps = {
  form: ReturnType<typeof useForm<StorageVolumeFormValues>>
  currentStorageLimit: number
  currentStorageCountLimit: number
  draftStorageLimit: number
  draftStorageCountLimit: number
  storageLimitPercentage: number
  storageCountPercentage: number
  isPending: boolean
  onCancelClick: () => void
  onConfirmClick: () => void
}

const EditModeContent = ({
  form,
  currentStorageLimit,
  currentStorageCountLimit,
  draftStorageLimit,
  draftStorageCountLimit,
  storageLimitPercentage,
  storageCountPercentage,
  isPending,
  onCancelClick,
  onConfirmClick,
}: EditModeContentProps) => {
  // Ensure maxSliderValues are never less than 10
  const maxStorageSliderValue = Math.max(
    Math.ceil(currentStorageLimit * 1.3),
    10
  )
  const maxStorageCountSliderValue = Math.max(
    Math.ceil(currentStorageCountLimit * 1.3),
    10
  )

  return (
    <Form {...form}>
      <form className="space-y-6">
        <div className="space-y-6">
          {/* Storage GB Limit Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Storage GB Limit</span>
              <Badge variant="secondary" className="px-2 py-0 text-sm">
                Editing
              </Badge>
            </div>

            <div className="flex items-center justify-between border border-border rounded-md p-3 mb-2 bg-muted/30">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Current Limit</p>
                <p className="text-xl font-bold">{currentStorageLimit}</p>
              </div>
              <ArrowRight className="text-muted-foreground h-5 w-5" />
              <div className="text-center">
                <p className="text-sm text-muted-foreground">New Limit</p>
                <p className="text-xl font-bold text-primary">
                  {draftStorageLimit}
                </p>
                <Badge className="bg-primary/20 text-primary hover:bg-primary/25 border-0">
                  {storageLimitPercentage > 0 ? '+' : ''}
                  {storageLimitPercentage}%
                </Badge>
              </div>
            </div>

            <FormField
              control={form.control}
              name="storageLimit"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between gap-4">
                    <div className="relative w-full">
                      <FormControl>
                        <Slider
                          value={[field.value]}
                          min={0}
                          max={maxStorageSliderValue}
                          step={1}
                          onValueChange={(value) => field.onChange(value[0])}
                          className="w-full"
                        />
                      </FormControl>
                      <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                        <span>0</span>
                        <span>{maxStorageSliderValue / 2}</span>
                        <span>{maxStorageSliderValue}</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FormControl>
                        <Input
                          type="number"
                          value={field.value === 0 ? '' : field.value}
                          onChange={(e) => {
                            const value =
                              e.target.value === '' ? 0 : Number(e.target.value)
                            field.onChange(value)
                          }}
                          className="w-32 text-xl font-bold text-center"
                          min={0}
                          data-testid="storage-gb-limit-input"
                        />
                      </FormControl>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Storage Count Limit Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Storage Volumes Limit</span>
            </div>

            <div className="flex items-center justify-between border border-border rounded-md p-3 mb-2 bg-muted/30">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Current Limit</p>
                <p className="text-xl font-bold">{currentStorageCountLimit}</p>
              </div>
              <ArrowRight className="text-muted-foreground h-5 w-5" />
              <div className="text-center">
                <p className="text-sm text-muted-foreground">New Limit</p>
                <p className="text-xl font-bold text-primary">
                  {draftStorageCountLimit}
                </p>
                <Badge className="bg-primary/20 text-primary hover:bg-primary/25 border-0">
                  {storageCountPercentage > 0 ? '+' : ''}
                  {storageCountPercentage}%
                </Badge>
              </div>
            </div>

            <FormField
              control={form.control}
              name="storageCountLimit"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between gap-4">
                    <div className="relative w-full">
                      <FormControl>
                        <Slider
                          value={[field.value]}
                          min={0}
                          max={maxStorageCountSliderValue}
                          step={1}
                          onValueChange={(value) => field.onChange(value[0])}
                          className="w-full"
                        />
                      </FormControl>
                      <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                        <span>0</span>
                        <span>{maxStorageCountSliderValue / 2}</span>
                        <span>{maxStorageCountSliderValue}</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FormControl>
                        <Input
                          type="number"
                          value={field.value === 0 ? '' : field.value}
                          onChange={(e) => {
                            const value =
                              e.target.value === '' ? 0 : Number(e.target.value)
                            field.onChange(value)
                          }}
                          className="w-32 text-xl font-bold text-center"
                          min={0}
                          data-testid="storage-volumes-limit-input"
                        />
                      </FormControl>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <WarningBox />

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancelClick}
            data-testid="cancel-storage-limit-button"
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={onConfirmClick}
            disabled={isPending || !form.formState.isValid}
            data-testid="apply-storage-limit-button"
          >
            {isPending ? (
              <div className="flex items-center gap-2">
                <Loader className="h-4 w-4 animate-spin" />
                <span>Processing...</span>
              </div>
            ) : (
              'Apply to All Users'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}

const WarningBox = () => (
  <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 flex gap-2">
    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
    <div className="text-sm">
      <p className="font-medium text-destructive">Warning</p>
      <p className="text-muted-foreground">
        Updating the organization-wide limit will override all existing custom
        user limits.
      </p>
    </div>
  </div>
)
